import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Card, FakeText, Pagination } from "@totalsoft/rocket-ui";
import { Grid } from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import FilterListIcon from "@mui/icons-material/FilterList";
import { loadPartnerLogs } from "features/partner/actionCreators";
import { useParams } from "react-router-dom";
import FiltersPartnerLog from "./FiltersPartnerLog";
import ListLog from "./ListLog";
import { getPartnerLogs } from "features/partner/selectors";
import debounce from "lodash.debounce";

const initialFilters = {
  partnerId: "",
  startDate: null,
  endDate: null,
  userName: "",
  changeType: ""
};

const PartnerLogContainer = ({
  actions,
  filters,
  partnerLogs,
  loading,
  pager,
  totalCount,
  onFiltersChanged,
  onChangePage,
  onChangeRowsPerPage
}) => {
  const [editFilters, setEditFilters] = useState(filters);
  const debouncedEditFilters = useRef(debounce(onFiltersChanged, 250));

  useEffect(() => {
    actions.loadPartnerLogs(filters, pager.page, pager.pageSize);
  }, [actions, filters, pager.page, pager.pageSize]);

  useEffect(() => {
    debouncedEditFilters.current(editFilters);
  }, [editFilters]);

  const onRefresh = () => {
    onChangePage(0);
  };

  const handleChangeFilterValue = (name, value) => {
    setEditFilters({ ...editFilters, [name]: value });
  };

  return (
    <Grid container>
      <Card icon={FilterListIcon} iconColor="primary">
        <FiltersPartnerLog filters={editFilters} onChangeFilterValue={handleChangeFilterValue} />
      </Card>
      <Card icon={ListIcon} iconColor="primary">
        <>
          {loading ? (
            <FakeText lines={10} />
          ) : (
            <Grid container spacing={1}>
              {partnerLogs.map(partnerLog => {
                return <ListLog key={partnerLog.id} partnerLog={partnerLog} />;
              })}
            </Grid>
          )}

          <Pagination
            loading={loading}
            count={totalCount}
            pageSize={pager.pageSize}
            page={pager.page - 1}
            onRefresh={onRefresh}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
        </>
      </Card>
    </Grid>
  );
};

PartnerLogContainer.propTypes = {
  actions: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  partnerLogs: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  pager: PropTypes.object.isRequired,
  totalCount: PropTypes.number.isRequired,
  onFiltersChanged: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeRowsPerPage: PropTypes.func.isRequired
};

const mapStateToProps = (state, ownProps) => {
  const { filters, pager } = ownProps;
  const { loading, content: partnerLogs, totalCount } = getPartnerLogs(state, filters, pager.page, pager.pageSize);
  return {
    partnerLogs,
    totalCount,
    loading
  };
};

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators({ loadPartnerLogs }, dispatch)
  };
};

const PartnerLogContainerConnected = connect(mapStateToProps, mapDispatchToProps)(PartnerLogContainer);

const PartnerLogContainerWrapper = () => {
  const params = useParams();
  const partnerId = params.partnerId;

  const [pager, setPager] = useState({ page: 1, pageSize: 10 });
  const [filters, setFilters] = useState({ ...initialFilters, partnerId });

  const pagerUpdater = useCallback(
    (prop, value) => {
      return prop === "pageSize" ? { ...pager, [prop]: value, page: 1 } : { ...pager, [prop]: value };
    },
    [pager]
  );

  const onChangePage = page => {
    setPager(pagerUpdater("page", page + 1));
  };

  const onChangeRowsPerPage = pageSize => {
    setPager(pagerUpdater("pageSize", pageSize));
  };

  const handleFiltersChanged = useCallback(
    filters => {
      setFilters({ ...filters, partnerId });
      setPager(pagerUpdater("page", 1));
    },
    [pagerUpdater, partnerId]
  );

  return (
    <PartnerLogContainerConnected
      filters={filters}
      pager={pager}
      onFiltersChanged={handleFiltersChanged}
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangeRowsPerPage}
    />
  );
};

export default PartnerLogContainerWrapper;
